import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, useLocation } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

import { StepFlowLayout } from "~/components/layouts/StepFlowLayout";
import { useFlowStart } from "~/hooks/use-flow-start";
import { useStepCompleted } from "~/hooks/use-step-completed";
import { ACQUISITION_PARTNERS_ENABLED } from "~/lib/acquisition-partners";
import {
  claimAcquisitionReferral,
  claimOverstapperHook,
  claimReferral,
  providersListQueryOptions,
} from "~/lib/api";
import { captureAttribution } from "~/lib/attribution";
import { authClient } from "~/lib/auth";
import { isOffboarded } from "~/lib/customer-status";
import {
  captureOverstapperFromSearch,
  claimOverstapperHookIfNeeded,
} from "~/lib/overstapper";
import { REFERRAL_ENABLED } from "~/lib/viral-loops";
import { BRANCH_FLOWS, resolveBranch } from "./-journey";

const onboardingSearchSchema = z.object({
  provider: z.string().optional(),
  model: z.string().optional(),
  via: z.string().optional(),
  type: z.string().optional(),
  error: z.string().optional(),
  referralCode: z.string().optional(),
  // A child acquisition partner's code from the wervingsportaal (see portal/src/lib/links.ts).
  // Kept out of `referralCode`, which belongs to Viral Loops and its widget.
  partnerCode: z.string().optional(),
});

type OnboardingSearch = z.infer<typeof onboardingSearchSchema>;

/** The search without one claimed code, so a reload doesn't claim it again. */
function without(
  search: OnboardingSearch,
  key: "referralCode" | "partnerCode",
): OnboardingSearch {
  const rest = { ...search };
  delete rest[key];
  return rest;
}

export const Route = createFileRoute("/(auth-pages)/onboarding")({
  validateSearch: onboardingSearchSchema,
  beforeLoad: async ({ location, search }) => {
    // Before any redirect below, which drops the entry URL's search (e.g. the bounce
    // to provider-selection). Idempotent, so every navigation can run it.
    captureOverstapperFromSearch(location.search as Record<string, unknown>);

    const { data: session } = await authClient.getSession();

    // Reactivation is admin-triggered by design, so an offboarded customer must
    // not be able to walk back in through onboarding.
    if (isOffboarded(session?.user.status)) {
      throw redirect({ to: "/account-inactief" });
    }

    if (session?.user.isOnboarded) {
      // Some OAuth callbacks (e.g. Zaptec) redirect here hardcoded; onboarded
      // users adding a charger continue in the /mijn-laadpaal modal instead.
      if (location.pathname.endsWith("link-charger") && search.provider) {
        throw redirect({
          to: "/mijn-laadpaal",
          search: { connect: search.provider, error: search.error },
          replace: true,
        });
      }
      throw redirect({ to: "/dashboard" });
    }

    // First visit: bootstrap an anonymous user.
    if (!session) await authClient.signIn.anonymous();

    // The anonymous user keeps its id through email verification (betterauth
    // `onLinkAccount` updates in place), so this already keys to the real customer.
    // Each redirect drops only its own code; a link carrying both claims the other
    // on the next pass through this guard.
    if (ACQUISITION_PARTNERS_ENABLED && search.partnerCode) {
      await claimAcquisitionReferral({ code: search.partnerCode }).catch(
        (error: unknown) =>
          console.warn("Child acquisition partner referral claim failed", error),
      );

      throw redirect({
        to: "/onboarding/$step",
        params: { step: "provider-selection" },
        search: without(search, "partnerCode"),
        replace: true,
      });
    }

    if (REFERRAL_ENABLED && search.referralCode) {
      await claimReferral({ referredByCode: search.referralCode, source: "link" }).catch(
        (error: unknown) => console.warn("Referral claim failed", error),
      );

      throw redirect({
        to: "/onboarding/$step",
        params: { step: "provider-selection" },
        search: without(search, "referralCode"),
        replace: true,
      });
    }

    if (session) return;
    if (location.pathname === `/onboarding/provider-selection`) return;
    // Marketing deep link (MID check widget): the brand is already chosen, so a
    // first-time visitor goes straight to the connect-method chooser instead of
    // being bounced back to pick it again.
    if (location.pathname === `/onboarding/connect-method` && search.provider) return;
    throw redirect({
      to: "/onboarding/$step",
      params: { step: "provider-selection" },
    });
  },
  // Resolved before render so the connect-method step never shows a brand with
  // its own backoffice as if it had none; a failed fetch falls back to the CPOs.
  loader: ({ context }) =>
    context.queryClient.query(providersListQueryOptions()).catch(() => undefined),
  component: OnboardingLayout,
});

function OnboardingLayout() {
  const { provider: providerId } = Route.useSearch();
  const location = useLocation();
  const brands = useQuery(providersListQueryOptions()).data?.providers ?? [];
  const provider = providerId ? brands.find((b) => b.slug === providerId) : null;

  // Attribution params ride in on the entry URL; register them as OpenPanel
  // global properties so the whole funnel can be split by partner/campaign.
  // Runs once on mount — later steps drop the params from the URL.
  // The overstapper flag is captured in beforeLoad (before any redirect); here it is
  // claimed on the API once a session exists (anonymous id survives email OTP).
  useEffect(() => {
    const search = location.search as Record<string, unknown>;
    captureAttribution(search);
    void claimOverstapperHookIfNeeded(claimOverstapperHook);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useFlowStart("onboarding");

  // Route.useParams() can return stale params during transitions.
  // Use location.pathname (and provider capability for pre-branch screens)
  // to determine the active flow.
  const branch = resolveBranch(location.pathname, provider);
  const STEPS = BRANCH_FLOWS[branch];

  let step = STEPS.find((s) => location.pathname.endsWith(s)) ?? STEPS[0];
  if (!provider) {
    step = STEPS[0];
  }
  const stepIndex = STEPS.indexOf(step);
  useStepCompleted("onboarding", stepIndex, STEPS, { branch });

  return (
    <StepFlowLayout
      currentStep={stepIndex + 1}
      totalSteps={STEPS.length}
      showBack={stepIndex > 0}
      showCornerPattern={true}
    >
      <Outlet />
    </StepFlowLayout>
  );
}
